import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type {
  BlacklistedPerson,
  BlackListLog,
} from 'commons/modules/service/black-list';
import type { CatalogClient } from 'api/client/models';

import type { RootState } from './configureStore';
import type { BlackListStoreType } from './types/black-list-store';

const initialState: BlackListStoreType = {
  persons: [],
  inputValue: '',
  filterStatus: '',
  filterLogStatus: '',
  refreshData: 0,
  blackListLog: undefined,
  blackListLoglast: undefined,
  showDetail: false,
  onlyQuery: false,
  catalogClients: [],
};

export const slice = createSlice({
  name: 'blackList',
  initialState: { ...initialState },
  reducers: {
    setPersons: (
      blackList: BlackListStoreType,
      action: PayloadAction<BlacklistedPerson[]>,
    ) => {
      blackList.persons = action.payload;
    },
    setInputValue: (
      blackList: BlackListStoreType,
      action: PayloadAction<string>,
    ) => {
      blackList.inputValue = action.payload;
    },
    setFilterStatus: (
      blackList: BlackListStoreType,
      action: PayloadAction<string>,
    ) => {
      blackList.filterStatus = action.payload;
    },
    setFilterLogStatus: (
      blackList: BlackListStoreType,
      action: PayloadAction<string>,
    ) => {
      blackList.filterLogStatus = action.payload;
    },
    setRefreshData: (
      blackList: BlackListStoreType,
      action: PayloadAction<number>,
    ) => {
      blackList.refreshData = action.payload;
    },
    setBlackListLogLast: (
      blackList: BlackListStoreType,
      action: PayloadAction<BlackListLog | undefined>,
    ) => {
      blackList.blackListLoglast = action.payload;
    },
    openDetail: (
      blackList: BlackListStoreType,
      action: PayloadAction<BlackListLog>,
    ) => {
      blackList.blackListLog = action.payload;
      blackList.showDetail = true;
    },
    closeDetail: (blackList: BlackListStoreType) => {
      blackList.blackListLog = undefined;
      blackList.showDetail = false;
      blackList.inputValue = '';
      blackList.filterStatus = '';
    },
    setOnlyQuery: (
      blackList: BlackListStoreType,
      action: PayloadAction<boolean>,
    ) => {
      blackList.onlyQuery = action.payload;
    },
    setCatalogClients: (
      blackList: BlackListStoreType,
      action: PayloadAction<CatalogClient[]>,
    ) => {
      blackList.catalogClients = action.payload;
    },
    cleanData: () => initialState,
  },
});

export const {
  setPersons,
  cleanData,
  setInputValue,
  setFilterStatus,
  setRefreshData,
  setBlackListLogLast,
  openDetail,
  closeDetail,
  setOnlyQuery,
  setFilterLogStatus,
  setCatalogClients,
} = slice.actions;

export default slice.reducer;

// Selectors
export const getPersons = (state: RootState) =>
  state.entities.blackList.persons;
export const getInputValue = (state: RootState) =>
  state.entities.blackList.inputValue;
export const getFilterStatus = (state: RootState) =>
  state.entities.blackList.filterStatus;
export const getFilterLogStatus = (state: RootState) =>
  state.entities.blackList.filterLogStatus;
export const getRefreshData = (state: RootState) =>
  state.entities.blackList.refreshData;
export const getBlackListLog = (state: RootState) =>
  state.entities.blackList.blackListLog;
export const getBlackListLoglast = (state: RootState) =>
  state.entities.blackList.blackListLoglast;
export const getShowDetail = (state: RootState) =>
  state.entities.blackList.showDetail;
export const getOnlyQuery = (state: RootState) =>
  state.entities.blackList.onlyQuery;
export const getCatalogClients = (state: RootState) =>
  state.entities.blackList.catalogClients;
